import PropTypes from 'prop-types';
import debounce from 'lodash.debounce';
import React, { memo, useCallback, useContext, useEffect, useState } from 'react';
import { Box, Label, SearchAutocomplete } from '@honda-canada/design-system-react';
import { UserLocationContext } from '@honda-canada/user-location';
import { useTranslation } from 'react-i18next';

import UserLocationButton from '../UserLocationButton';
import formatAddress from '../../utils/formatAddress';
import themeStyles from './DealerLocator.style';
import { DEBOUNCE_WAIT_TIME, SEARCH_ID, ZERO_RESULTS_ERROR, USER_PLACE_LOCALSTORAGE_KEY } from '../../utils/constants';
import { DealersListContext } from '../DealersListContext';
import { INPUT_SUGGESTION, pushGtmDealerSearchErrors, pushGtmDealerSearchType } from '../../utils/gtmTags';

const InputLabel = themeStyles.apply(Label, 'InputLabel');

const DealerLocatorInput = ({ setIsOpen, setSelectedPlace, inputValue, setInputValue, setSelectedDealer }) => {
  const [predictions, setPredictions] = useState([]);
  const [options, setOptions] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const { t } = useTranslation();

  const {
    getAutocompletePredictions,
    establishUserLocationFromAutocompleteSearch,
    error: errorStatus,
    resetError,
  } = useContext(UserLocationContext);

  const { productLinesSelection } = useContext(DealersListContext) || {};

  const changeHandler = (e, callback) => {
    callback(e.target.value, items => {
      setPredictions(items);
    });
  };

  const onInputReset = () => {
    setInputValue('');
    resetError();
  };

  const debouncedCallback = useCallback(debounce(changeHandler, DEBOUNCE_WAIT_TIME), []);

  useEffect(() => {
    const formattedOptions = predictions.map(({ description, place_id: placeId }) => ({
      text: description,
      value: placeId,
    }));
    setOptions(formattedOptions);

    return () => {
      setOptions([]);
    };
  }, [predictions]);

  useEffect(() => {
    if (!errorStatus) {
      setErrorMessage('');
    }
    if (errorStatus === ZERO_RESULTS_ERROR) {
      setErrorMessage(t('Sub-apps.Dealers.locationNotFoundError'));
      setOptions([]);
      pushGtmDealerSearchErrors(inputValue);
    }
  }, [errorStatus, t]);

  return (
    <Box mb="l" data-testid="cy-dealer-locator-input">
      <InputLabel data-testid="cy-dealer-locator-input-label">{t('Sub-apps.Dealers.enterLocationLabel')}</InputLabel>
      <SearchAutocomplete
        data-testid="cy-location-search-input"
        id={SEARCH_ID}
        placeholder={t('Sub-apps.Dealers.locationPlaceholder')}
        errorMessage={errorMessage}
        hasError={!!errorMessage}
        inputValue={inputValue}
        onChange={e => {
          setInputValue(e.target.value);
          resetError();
          debouncedCallback(e, getAutocompletePredictions);
        }}
        onSelectOption={option => {
          setOptions([]);
          establishUserLocationFromAutocompleteSearch(
            option.value,
            place => {
              localStorage.setItem(USER_PLACE_LOCALSTORAGE_KEY, JSON.stringify(place));
              setInputValue(formatAddress(place));
              setSelectedPlace(place);
              setSelectedDealer(null);
              pushGtmDealerSearchType(INPUT_SUGGESTION, place, productLinesSelection);
            },
            false,
            true,
          );
          setIsOpen(false);
        }}
        onInputReset={onInputReset}
        defaultValue={0}
        options={options}
        styling="secondary"
        ariaLabel={t('Sub-apps.Dealers.locationPlaceholder')}
        autoFocus
      />
      <UserLocationButton
        setSelectedDealer={setSelectedDealer}
        setIsOpen={setIsOpen}
        setSelectedPlace={setSelectedPlace}
        setInputValue={setInputValue}
      >
        {t('Sub-apps.Dealers.useMyLocationButton')}
      </UserLocationButton>
    </Box>
  );
};

DealerLocatorInput.propTypes = {
  setSelectedPlace: PropTypes.func,
  setIsOpen: PropTypes.func,
  setInputValue: PropTypes.func,
  inputValue: PropTypes.string,
  setSelectedDealer: PropTypes.func,
};

export default memo(DealerLocatorInput);
